<template>
    <div>
        <h3>
            댓글
            <span class="text-base text-gray-600"
                >({{ comments.length | numberFormat }})</span
            >
        </h3>
        <div
            v-for="(comment, i) in comments"
            :key="comment.id"
            class="mt-2 mb-4 py-3"
            :class="{ 'border-t border-gray-200': i > 0 }"
        >
            <div class="flex items-center justify-between">
                <div class="text-sm text-gray-800 tracking-tight">
                    {{ comment.username }}
                    <span class="text-xs text-gray-500"
                        >@{{ comment.updated_at }}</span
                    >
                </div>
                <div class="flex items-center text-gray-400">
                    <button
                        v-if="comment.id !== form.id"
                        type="button"
                        @click="moveForm(comment)"
                        class="hover:text-gray-800"
                    >
                        <svg
                            class="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <title>수정</title>
                            <path
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            ></path>
                        </svg>
                    </button>
                    <button
                        type="button"
                        @click="deleteComment(comment)"
                        class="ml-1 hover:text-gray-800"
                    >
                        <svg
                            class="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                        >
                            <title>삭제</title>
                            <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            <section class="mt-2 text-sm whitespace-pre-line leading-tight">
                {{ comment.body }}
            </section>

            <div :id="`slot${comment.id}`" class="mt-2"></div>
        </div>

        <div id="slot">
            <div class="mt-6 mb-3">
                <div class="flex items-center">
                    <div :class="{ 'w-2/3': !authenticated }">
                        <input
                            type="text"
                            placeholder="이름"
                            v-model.trim="$v.form.username.$model"
                            class="my-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                            :class="{
                                'border-red-500': $v.form.username.$error,
                            }"
                        />
                        <div
                            v-if="errors.username"
                            class="text-red-500 text-xs italic"
                        >
                            {{ errors.username[0] }}
                        </div>
                        <template v-if="$v.form.username.$error">
                            <div
                                v-if="!$v.form.username.required"
                                class="text-red-500 text-xs italic"
                            >
                                필수입력 항목입니다.
                            </div>
                            <div
                                v-if="!$v.form.username.minLength"
                                class="text-red-500 text-xs italic"
                            >
                                {{ $v.form.username.$params.minLength.min }}자
                                이상 입력하세요.
                            </div>
                        </template>
                    </div>
                    <div v-if="!authenticated" class="w-1/3 pl-1">
                        <input
                            type="password"
                            placeholder="비밀번호"
                            v-model.trim="$v.form.password.$model"
                            class="my-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                            :class="{
                                'border-red-500': $v.form.password.$error,
                            }"
                        />
                        <div
                            v-if="errors.password"
                            class="text-red-500 text-xs italic"
                        >
                            {{ errors.password[0] }}
                        </div>
                        <template v-if="$v.form.password.$error">
                            <div
                                v-if="!$v.form.password.required"
                                class="text-red-500 text-xs italic"
                            >
                                필수입력 항목입니다.
                            </div>
                            <div
                                v-if="!$v.form.password.minLength"
                                class="text-red-500 text-xs italic"
                            >
                                {{ $v.form.password.$params.minLength.min }}자
                                이상 입력하세요.
                            </div>
                        </template>
                    </div>
                </div>
                <div class="">
                    <textarea
                        id="body"
                        v-model.trim="$v.form.body.$model"
                        rows="3"
                        placeholder="댓글"
                        class="my-1 form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        :class="{ 'border-red-500': $v.form.body.$error }"
                    ></textarea>
                    <div
                        v-if="$v.form.body.$error || errors.body"
                        class="text-red-500 text-xs italic"
                    >
                        필수입력 항목입니다.
                    </div>
                </div>
                <div
                    class="flex items-start"
                    :class="this.form.id ? 'justify-end' : 'justify-between'"
                >
                    <div v-if="needCaptcha">
                        <div class="my-1 flex items-center">
                            <input
                                type="text"
                                maxlength="4"
                                v-model.trim="$v.form.captcha.$model"
                                placeholder="스팸방지 문자"
                                class="form-input block w-1/3 py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                :class="{
                                    'border-red-500': $v.form.captcha.$error,
                                }"
                            />
                            <get-captcha ref="captchaComponent" />
                        </div>
                        <div
                            v-if="errors.captcha"
                            class="text-red-500 text-xs italic"
                        >
                            {{ errors.captcha[0] }}
                        </div>
                        <template v-if="$v.form.captcha.$error">
                            <div
                                v-if="!$v.form.captcha.required"
                                class="text-red-500 text-xs italic"
                            >
                                필수입력 항목입니다.
                            </div>
                            <div
                                v-if="!$v.form.captcha.minLength"
                                class="text-red-500 text-xs italic"
                            >
                                {{ $v.form.captcha.$params.minLength.min }}자
                                이상 입력하세요.
                            </div>
                        </template>
                    </div>
                    <div v-if="this.form.id" class="mt-1">
                        <button
                            @click="newForm"
                            class="py-2 px-4 mr-1 border border-transparent text-sm transition duration-150 ease-in-out"
                        >
                            취소
                        </button>
                    </div>
                </div>
                <div class="mt-1">
                    <button
                        type="button"
                        @click="onSubmit"
                        class="inline-flex justify-center py-2 px-8 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                        :disabled="$v.$invalid || saving"
                        :class="{
                            'opacity-50 cursor-not-allowed':
                                $v.$invalid || saving,
                        }"
                    >
                        {{ saving ? '처리 중...' : buttonText }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import { validationMixin } from 'vuelidate'
import { required, minLength, minValue } from 'vuelidate/lib/validators'
import GetCaptcha from './GetCaptcha'

export default {
    components: {
        Editor,
        GetCaptcha,
    },

    mixins: [validationMixin],

    props: {
        node: String,
        postId: Number,
        auth: String,
    },

    data() {
        return {
            saving: false,
            errors: [],
            oldName: 'slot',
            comments: [],
            form: {
                id: 0,
                username: null,
                password: null,
                body: null,
                captcha: null,
            },
        }
    },

    validations() {
        let validator = {
            form: {
                body: {
                    required,
                },
                username: {
                    required,
                    minLength: minLength(2),
                },
            },
        }

        if (!this.authenticated) {
            validator.form['password'] = {
                required,
                minLength: minLength(4),
            }
        }
        if (this.needCaptcha) {
            validator.form['captcha'] = {
                required,
                minLength: minLength(4),
            }
        }

        return validator
    },

    created() {
        this.fetchData()

        if (this.auth) {
            this.form.username = this.auth
        }
    },

    computed: {
        authenticated() {
            return this.auth !== ''
        },

        needCaptcha() {
            return this.auth === '' && !this.form.id
        },

        buttonText() {
            return this.form.id ? '댓글 수정' : '댓글 남기기'
        },

        ready() {
            return (
                this.needCaptcha &&
                !this.$v.form.body.$invalid &&
                !this.$v.form.username.$invalid &&
                !this.$v.form.password.$invalid
            )
        },

        endpoint() {
            return this.form.id
                ? `/comments/${this.form.id}`
                : `/posts/${this.node}/${this.postId}/comments`
        },
    },

    watch: {
        ready(val) {
            if (!val || !this.needCaptcha) {
                return false
            }

            this.resetCaptcha()
        },
    },

    methods: {
        resetValidation() {
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },

        resetCaptcha() {
            this.$refs.captchaComponent.getCaptcha()
        },

        clearCaptcha() {
            this.$refs.captchaComponent.clearCaptcha()
        },

        moveForm(comment) {
            const oldSlot = document.getElementById(this.oldName)
            const newName = `slot${comment.id}`
            const newSlot = document.getElementById(newName)

            newSlot.appendChild(oldSlot.childNodes[0])
            this.form = Object.assign({}, comment)
            this.resetValidation()
            this.oldName = newName
        },

        newForm() {
            const oldSlot = document.getElementById(this.oldName)
            const newSlot = document.getElementById('slot')

            newSlot.appendChild(oldSlot.childNodes[0])
            this.resetComment()
            this.oldName = 'slot'
        },

        resetComment() {
            this.errors = []
            this.form = {
                id: 0,
                username: null,
                password: null,
                body: null,
                captcha: null,
            }
            this.resetValidation()
        },

        async fetchData() {
            const response = await axios.get(
                `/posts/${this.node}/${this.postId}/comments`
            )

            this.comments = response.data.data
        },

        async onSubmit() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                return false
            }

            this.saving = true

            try {
                const method = this.form.id ? 'patch' : 'post'
                const response = await axios[method](this.endpoint, this.form)

                this.saving = false

                if (this.form.id) {
                    const idx = this.comments.findIndex(
                        x => x.id === this.form.id
                    )
                    this.comments[idx] = response.data
                    this.newForm()
                    this.toast('댓글을 수정했습니다.')
                } else {
                    this.comments.push(response.data)
                    this.resetComment()
                    if (this.needCaptcha) {
                        this.clearCaptcha()
                    }
                    this.toast('댓글을 등록했습니다.')
                }
            } catch (error) {
                this.saving = false
                if (error.response.status && error.response.status !== 422) {
                    console.log('알 수 없는 오류가 발생했습니다.')
                }
                this.errors = error.response.data.errors || []
                if (this.needCaptcha) {
                    this.resetCaptcha()
                }
            }
        },

        async deleteComment(comment) {
            const option = {
                text: '정말 삭제할까요?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e02424',
                confirmButtonText: '삭제합니다',
                cancelButtonText: '취소',
            }

            if (!this.authenticated) {
                option.input = 'password'
                option.inputPlaceholder = '비밀번호를 입력하세요.'
            }

            const result = await this.$swal(option)

            if (!result.value) {
                return
            }

            const password = result.value

            try {
                const response = await axios.delete(`/comments/${comment.id}`, {
                    data: { password },
                })
                const idx = this.comments.findIndex(x => x.id === comment.id)
                this.comments.splice(idx, 1)
                this.resetComment()
                this.toast('댓글을 삭제했습니다.')
            } catch (error) {
                this.toast(error.response.data.message, 'error', 5000)
            }
        },

        toast(message, type, timer) {
            this.$swal({
                text: message,
                icon: type || 'success',
                confirmButtonText: '확인',
                timer: timer || 3000,
            })
        },
    },
}
</script>
